import { useState } from 'react'
import {
  DataGridEditableCell,
  editableActions,
  editableColumn,
} from '../../../core/component'
import { confirm, notify, translate } from '../../../core'
import { Button, Form, Input, message, Space, Table, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import { TicketCategoryEntry } from '../type/ticket-category'
import { ticketCategoryEntityToEntry, ticketCategoryEntryToDto } from '../model-mapper/ticket-category-mapper'
import {
  GetTicketCategoriesQuery,
  CreateTicketCategoryMutation,
  UpdateTicketCategoryMutation,
  DeleteTicketCategoryMutation
} from '../graphql/ticket-category.graphql'
import { BadRequestException } from '../../../core/type'

export const TicketCategoryList = () => {
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [editingKey, setEditingKey] = useState<string>('')
  const [pageSize, setPageSize] = useState<number>(10)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [newRowKey, setNewRowKey] = useState<string | null>(null)
  
  const isEditing = (record: TicketCategoryEntry) => record.key === editingKey

  const { data, loading, refetch } = useQuery(GetTicketCategoriesQuery, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
      }
    }
  })

  const [createMutation, { loading: creating }] = useMutation(CreateTicketCategoryMutation, {
    onCompleted: () => {
      messageApi.success(translate('ticket_category', 'save_success_message'))
      setEditingKey('')
      setNewRowKey(null)
      refetch()
    },
    onError: (error) => {
      messageApi.error(error.message)
    },
  })

  const [updateMutation, { loading: updating }] = useMutation(UpdateTicketCategoryMutation, {
    onCompleted: () => {
      messageApi.success(translate('ticket_category', 'save_success_message'))
      setEditingKey('')
      refetch()
    },
    onError: (error) => {
      messageApi.error(error.message)
    },
  })

  const [deleteMutation, { loading: deleting }] = useMutation(DeleteTicketCategoryMutation, {
    onCompleted: () => {
      messageApi.success(translate('ticket_category', 'remove_success_message'))
      refetch()
    },
    onError: (error) => {
      messageApi.error(error.message)
    },
  })

  const handleAdd = () => {
    const key = `new-${Date.now()}`
    setNewRowKey(key)
    setEditingKey(key)
    form.resetFields()
    form.setFieldsValue({ title: '' })
  }

  const handleEdit = (record: Partial<TicketCategoryEntry>) => {
    form.setFieldsValue(record)
    setEditingKey(record.key ?? '')
  }

  const handleCancel = (record: TicketCategoryEntry) => {
    setEditingKey('')
    if (newRowKey === record.key) {
      setNewRowKey(null)
    }
    form.resetFields()
  }

  const handleSave = async (record: TicketCategoryEntry) => {
    try {
      const row = await form.validateFields() as TicketCategoryEntry
      const dto = ticketCategoryEntryToDto({ ...record, ...row })

      if (newRowKey === record.key) {
        await createMutation({
          variables: { dto },
        })
      } else {
        await updateMutation({
          variables: {
            id: record.id,
            dto,
          },
        })
      }
    } catch (e) {
      if (e instanceof BadRequestException) {
        notify.error(
          translate('wrong_message', 'ticket_category'),
          [e.message].flat().join('<br/>'),
        )
      }
    }
  }

  const handleRemove = async (record: TicketCategoryEntry) => {
    const result = await confirm(
      translate('remove_confirmation_message', 'ticket_category', '?'),
    )

    if (!result)
      return

    await deleteMutation({
      variables: {
        id: record.id,
      },
    })
  }

  const dataSource = [
    ...(newRowKey ? [{
      key: newRowKey,
      id: 0,
      title: '',
    } as TicketCategoryEntry] : []),
    ...(data?.ticketCategoriesFind.data ?? []).map(ticketCategoryEntityToEntry)
  ]

  return (
    <div className="p-2">
      {contextHolder}
      <Space className="w-100 justify-content-end p-2">
        <Tooltip title={translate('add')}>
          <Button
            icon={<PlusOutlined/>}
            onClick={handleAdd}
            disabled={!!editingKey}
          />
        </Tooltip>
      </Space>

      <Form form={form} component={false}>
        <Table<TicketCategoryEntry>
          loading={loading}
          columns={[
            {
              dataIndex: 'id',
              title: translate('id'),
              width: 200,
              render: (value: number) => value || '-',
            },
            editableColumn<TicketCategoryEntry>({
              dataIndex: 'title',
              title: translate('title'),
              editableComponent: (
                <Form.Item
                  name="title"
                  rules={[
                    {
                      required: true,
                      message: translate('missing_field_message', 'title'),
                    },
                  ]}
                >
                  <Input/>
                </Form.Item>
              ),
              isEditing,
            }),
            editableActions(
              editingKey,
              creating || updating,
              isEditing,
              handleCancel,
              handleEdit,
              handleSave,
              handleRemove,
              deleting,
            ),
          ]}
          dataSource={dataSource}
          pagination={{
            total: data?.ticketCategoriesFind.count ?? 0,
            position: ['bottomCenter'],
            pageSize: pageSize,
            showSizeChanger: true,
          }}
          onChange={(pagination) => {
            setEditingKey('')
            if (pagination.current)
              setCurrentPage(pagination.current - 1)

            if (pagination.pageSize)
              setPageSize(pagination.pageSize)
          }}
          components={{
            body: {
              cell: DataGridEditableCell,
            },
          }}
        />
      </Form>
    </div>
  )
}
